
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Vehicle {
  id: number;
  name: string;
  year: number;
  make: string;
  model: string;
  price: number;
  mileage: number;
  location: string;
  badge?: string;
  transmission: string;
  fuelType: string;
  bodyType: string;
  color: string;
  images: string[];
}

const DB_NAME = 'CarInventoryDB';
const DB_VERSION = 1;
const STORE_NAME = 'carImages';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

async function saveToIndexedDB(key: string, data: any): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(data, key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function loadFromIndexedDB(key: string): Promise<any> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [bulkImageUrls, setBulkImageUrls] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMake, setFilterMake] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const savedVehicles = localStorage.getItem('vehicles');
      const savedImages = await loadFromIndexedDB('car-inventory-images');

      if (savedVehicles) {
        const parsedVehicles = JSON.parse(savedVehicles);

        if (savedImages && Array.isArray(savedImages)) {
          const mergedVehicles = parsedVehicles.map((vehicle: Vehicle) => {
            const savedCar = savedImages.find((c: any) => c.id === vehicle.id);
            if (savedCar && savedCar.images && savedCar.images.length > 0) {
              return {
                ...vehicle,
                images: savedCar.images.map((img: any) => (typeof img === 'string' ? img : img.url)),
              };
            }
            return vehicle;
          });
          setVehicles(mergedVehicles);
        } else {
          setVehicles(parsedVehicles);
        }
      }
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const handleSelectVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setEditMode(false);
    setBulkImageUrls('');
  };

  const handleEditVehicle = () => {
    if (selectedVehicle) {
      setEditMode(true);
      setBulkImageUrls(selectedVehicle.images.join('\n'));
    }
  };

  const handleUpdateField = (field: keyof Vehicle, value: any) => {
    if (selectedVehicle) {
      setSelectedVehicle({
        ...selectedVehicle,
        [field]: value,
      });
    }
  };

  const handleBulkImageUpload = () => {
    if (selectedVehicle) {
      const urls = bulkImageUrls
        .split('\n')
        .map((url) => url.trim())
        .filter((url) => url.length > 0);

      setSelectedVehicle({
        ...selectedVehicle,
        images: urls,
      });
    }
  };

  const handleSaveVehicle = async () => {
    if (!selectedVehicle) return;

    setSaveStatus('saving');

    try {
      const updatedVehicles = vehicles.map((v) =>
        v.id === selectedVehicle.id ? selectedVehicle : v
      );

      setVehicles(updatedVehicles);
      localStorage.setItem('vehicles', JSON.stringify(updatedVehicles));

      const savedImages = (await loadFromIndexedDB('car-inventory-images')) || [];
      const imageData = Array.isArray(savedImages) ? savedImages : [];

      const updatedImageData = imageData.filter((c: any) => c.id !== selectedVehicle.id);
      updatedImageData.push({
        id: selectedVehicle.id,
        images: selectedVehicle.images.map((url) => ({ url })),
      });

      await saveToIndexedDB('car-inventory-images', updatedImageData);

      window.dispatchEvent(new Event('vehiclesUpdated'));
      window.dispatchEvent(new Event('inventoryImagesUpdated'));

      setSaveStatus('saved');
      setEditMode(false);
      
      // Reload vehicles to ensure everything is in sync
      await loadVehicles();
      
      // Re-select the vehicle to show updated data
      const refreshedVehicle = updatedVehicles.find(v => v.id === selectedVehicle.id);
      if (refreshedVehicle) {
        setSelectedVehicle(refreshedVehicle);
      }
      
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving vehicle:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const handleSaveAll = async () => {
    setSaveStatus('saving');

    try {
      localStorage.setItem('vehicles', JSON.stringify(vehicles));

      const imageData = vehicles.map((vehicle) => ({
        id: vehicle.id,
        images: vehicle.images.map((url) => ({ url })),
      }));

      await saveToIndexedDB('car-inventory-images', imageData);

      window.dispatchEvent(new Event('vehiclesUpdated'));
      window.dispatchEvent(new Event('inventoryImagesUpdated'));

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving all vehicles:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const handleAddImage = () => {
    if (selectedVehicle) {
      setSelectedVehicle({
        ...selectedVehicle,
        images: [...selectedVehicle.images, ''],
      });
    }
  };

  const handleUpdateImage = (index: number, url: string) => {
    if (selectedVehicle) {
      const newImages = [...selectedVehicle.images];
      newImages[index] = url;
      setSelectedVehicle({
        ...selectedVehicle,
        images: newImages,
      });
    }
  };

  const handleRemoveImage = (index: number) => {
    if (selectedVehicle) {
      const newImages = selectedVehicle.images.filter((_, i) => i !== index);
      setSelectedVehicle({
        ...selectedVehicle,
        images: newImages,
      });
    }
  };

  const filteredVehicles = vehicles.filter((vehicle) => {
    const matchesSearch =
      searchQuery === '' ||
      vehicle.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vehicle.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesMake = filterMake === '' || vehicle.make === filterMake;

    return matchesSearch && matchesMake;
  });

  const uniqueMakes = Array.from(new Set(vehicles.map((v) => v.make))).sort();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Back to Website</span>
            </button>
            <div className="h-6 w-px bg-gray-300"></div>
            <h1 className="text-xl font-bold text-gray-900">Inventory Admin</h1>
          </div>

          <div className="flex items-center gap-3">
            {saveStatus === 'saving' && (
              <span className="text-sm text-gray-600 flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Saving...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="text-sm text-green-600 flex items-center gap-2">
                <i className="ri-check-line"></i>
                Saved!
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="text-sm text-red-600 flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                Error saving
              </span>
            )}
            <button
              onClick={handleSaveAll}
              disabled={saveStatus === 'saving'}
              className="px-6 py-2.5 bg-[#2563EB] text-white rounded-full font-medium hover:bg-[#1d4ed8] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save All Changes
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Sidebar - Vehicle List */}
          <div className="col-span-1 bg-white rounded-2xl p-6 h-[calc(100vh-180px)] overflow-y-auto">
            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Vehicles ({filteredVehicles.length})
              </h2>

              {/* Search */}
              <div className="relative mb-3">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-search-line text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search vehicles..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                />
              </div>

              {/* Make Filter */}
              <select
                value={filterMake}
                onChange={(e) => setFilterMake(e.target.value)}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm cursor-pointer focus:outline-none focus:border-[#2563EB]"
              >
                <option value="">All Makes</option>
                {uniqueMakes.map((make) => (
                  <option key={make} value={make}>
                    {make}
                  </option>
                ))}
              </select>
            </div>

            {/* Vehicle List */}
            <div className="space-y-2">
              {filteredVehicles.map((vehicle) => (
                <button
                  key={vehicle.id}
                  onClick={() => handleSelectVehicle(vehicle)}
                  className={`w-full text-left p-4 rounded-xl transition-all cursor-pointer ${
                    selectedVehicle?.id === vehicle.id
                      ? 'bg-[#2563EB] text-white'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      {vehicle.images[0] ? (
                        <img
                          src={vehicle.images[0]}
                          alt={vehicle.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-2xl text-gray-400"></i>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{vehicle.name}</p>
                      <p
                        className={`text-xs mt-1 ${
                          selectedVehicle?.id === vehicle.id ? 'text-white/80' : 'text-gray-500'
                        }`}
                      >
                        ${vehicle.price.toLocaleString()} • {vehicle.mileage.toLocaleString()} mi
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right Panel - Vehicle Editor */}
          <div className="col-span-2 bg-white rounded-2xl p-6 h-[calc(100vh-180px)] overflow-y-auto">
            {selectedVehicle ? (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">{selectedVehicle.name}</h2>
                  <div className="flex items-center gap-2">
                    {editMode ? (
                      <>
                        <button
                          onClick={() => {
                            setEditMode(false);
                            loadVehicles();
                          }}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveVehicle}
                          disabled={saveStatus === 'saving'}
                          className="px-4 py-2 bg-[#2563EB] text-white rounded-full font-medium hover:bg-[#1d4ed8] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          Save Vehicle
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleEditVehicle}
                        className="px-4 py-2 bg-[#2563EB] text-white rounded-full font-medium hover:bg-[#1d4ed8] transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Edit Vehicle
                      </button>
                    )}
                  </div>
                </div>

                {/* Vehicle Details Form */}
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                        <input
                          type="text"
                          value={selectedVehicle.name}
                          onChange={(e) => handleUpdateField('name', e.target.value)}
                          disabled={!editMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                        <input
                          type="number"
                          value={selectedVehicle.price}
                          onChange={(e) => handleUpdateField('price', Number(e.target.value))}
                          disabled={!editMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                        <input
                          type="number"
                          value={selectedVehicle.year}
                          onChange={(e) => handleUpdateField('year', Number(e.target.value))}
                          disabled={!editMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                        <input
                          type="text"
                          value={selectedVehicle.make}
                          onChange={(e) => handleUpdateField('make', e.target.value)}
                          disabled={!editMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                        <input
                          type="text"
                          value={selectedVehicle.model}
                          onChange={(e) => handleUpdateField('model', e.target.value)}
                          disabled={!editMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mileage</label>
                        <input
                          type="number"
                          value={selectedVehicle.mileage}
                          onChange={(e) => handleUpdateField('mileage', Number(e.target.value))}
                          disabled={!editMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                        <input
                          type="text"
                          value={selectedVehicle.location}
                          onChange={(e) => handleUpdateField('location', e.target.value)}
                          disabled={!editMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Badge</label>
                        <input
                          type="text"
                          value={selectedVehicle.badge || ''}
                          onChange={(e) => handleUpdateField('badge', e.target.value)}
                          disabled={!editMode}
                          placeholder="Optional"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Transmission
                        </label>
                        <select
                          value={selectedVehicle.transmission}
                          onChange={(e) => handleUpdateField('transmission', e.target.value)}
                          disabled={!editMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500 cursor-pointer"
                        >
                          <option value="Automatic">Automatic</option>
                          <option value="Manual">Manual</option>
                          <option value="CVT">CVT</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fuel Type</label>
                        <select
                          value={selectedVehicle.fuelType}
                          onChange={(e) => handleUpdateField('fuelType', e.target.value)}
                          disabled={!editMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500 cursor-pointer"
                        >
                          <option value="Gasoline">Gasoline</option>
                          <option value="Diesel">Diesel</option>
                          <option value="Hybrid">Hybrid</option>
                          <option value="Electric">Electric</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Body Type</label>
                        <select
                          value={selectedVehicle.bodyType}
                          onChange={(e) => handleUpdateField('bodyType', e.target.value)}
                          disabled={!editMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500 cursor-pointer"
                        >
                          <option value="Sedan">Sedan</option>
                          <option value="SUV">SUV</option>
                          <option value="Truck">Truck</option>
                          <option value="Van">Van</option>
                          <option value="Coupe">Coupe</option>
                          <option value="Convertible">Convertible</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                        <input
                          type="text"
                          value={selectedVehicle.color}
                          onChange={(e) => handleUpdateField('color', e.target.value)}
                          disabled={!editMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Images Section */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">
                        Images ({selectedVehicle.images.length})
                      </h3>
                      {editMode && (
                        <button
                          onClick={handleAddImage}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                        >
                          <i className="ri-add-line"></i>
                          Add Image
                        </button>
                      )}
                    </div>

                    {/* Bulk Upload */}
                    {editMode && (
                      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-upload-cloud-line text-[#2563EB]"></i>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">Bulk Image Upload</h4>
                            <p className="text-sm text-gray-600 mb-3">
                              Paste image URLs (one per line) to replace all images at once
                            </p>
                            <textarea
                              value={bulkImageUrls}
                              onChange={(e) => setBulkImageUrls(e.target.value)}
                              placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg&#10;https://example.com/image3.jpg"
                              rows={4}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] text-sm font-mono"
                            />
                            <button
                              onClick={handleBulkImageUpload}
                              className="mt-3 px-4 py-2 bg-[#2563EB] text-white rounded-full font-medium hover:bg-[#1d4ed8] transition-colors cursor-pointer whitespace-nowrap"
                            >
                              Apply Bulk Upload
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Individual Images */}
                    <div className="space-y-3">
                      {selectedVehicle.images.map((image, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                          <div className="w-20 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                            {image ? (
                              <img
                                src={image}
                                alt={`Image ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '';
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <i className="ri-image-line text-2xl text-gray-400"></i>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <input
                              type="text"
                              value={image}
                              onChange={(e) => handleUpdateImage(index, e.target.value)}
                              disabled={!editMode}
                              placeholder="Enter image URL"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#2563EB] disabled:bg-white disabled:text-gray-500 text-sm"
                            />
                          </div>
                          {editMode && (
                            <button
                              onClick={() => handleRemoveImage(index)}
                              className="w-10 h-10 flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors cursor-pointer flex-shrink-0"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-center">
                <div>
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-car-line text-4xl text-gray-400"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Vehicle Selected</h3>
                  <p className="text-gray-600">
                    Select a vehicle from the list to view and edit details
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
